import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import * as ClientConstants from '../../../core/constants/client.constants';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-fiscal-data',
  standalone: true,
  imports: [InputTextModule, DropdownModule, FormsModule, ReactiveFormsModule, CalendarModule, AccordionModule, CalendarModule, CheckboxModule, CommonModule, FontAwesomeModule],
  templateUrl: './fiscal-data.component.html',
  styleUrl: './fiscal-data.component.scss'
})


export class FiscalDataComponent {
  @Input() data: any;
  @Input() client: any;
  clientConstants = ClientConstants;
  hasCertificate: boolean = false;
  faTriangleExclamation = faTriangleExclamation;
  
  get isPhysicalPerson(): boolean {
    return this.client?.personType === 'PHYSICAL_PERSON';
  }

  isCertificateExpired(index: number): boolean {
    const certificate = this.digitalCertificates.at(index);
    if (!certificate) return false;
    
    const expiryDate = certificate.get('expiryDate')?.value;
    if (!expiryDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    
    return expiry < today;
  }

  activitiesForm = this.fb.group({
    activities: this.fb.array([]),
  });

  societyForm = this.fb.group({
    isBigCompany: [],
    isBigCompanyPeriods: this.fb.array([]),
    startDateISOObligation: [],
    fractionatedPayment: [],
    paymentOnAccountIS403LIS: [],
    paymentOnAccountIS403LISPeriods: this.fb.array([]),
  });

  otherForm = this.fb.group({
    id: [],
    client: [],
    administrationNameAEAT: [''],
    administrationCodeAEAT: [''],
    delegationAEAT: [''],
    intraCommunityOperatorObligation: [''],
    intraCommunityOperatorPeriods: this.fb.array([]),
    presentationSIIObligation: [''],
    presentationSIIPeriods: this.fb.array([]),
    codeLEI: [''],
    codeLEIPeriods: this.fb.array([]),
    codeEORI: [''],
    codeEORIPeriods: this.fb.array([]),
    isFiscalGroup: [false],
    fiscalGroupPeriods: this.fb.array([]),
    digitalCertificates: this.fb.array([]),
});

  get is403LISPeriods(): FormArray {
    return this.societyForm.get('paymentOnAccountIS403LISPeriods') as FormArray;
  }

  get bigCompanyPeriods(): FormArray {
    return this.societyForm.get('isBigCompanyPeriods') as FormArray;
  }

  get roiPeriods(): FormArray {
    return this.otherForm.get('intraCommunityOperatorPeriods') as FormArray;
  }

  get leiPeriods(): FormArray {
    return this.otherForm.get('codeLEIPeriods') as FormArray;
  }

  get eoriPeriods(): FormArray {
    return this.otherForm.get('codeEORIPeriods') as FormArray;
  }

  get siiPeriods(): FormArray {
    return this.otherForm.get('presentationSIIPeriods') as FormArray;
  }

  get fiscalGroupPeriods(): FormArray {
    return this.otherForm.get('fiscalGroupPeriods') as FormArray;
  }

  get digitalCertificates(): FormArray {
    return this.otherForm.get('digitalCertificates') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
  ) { }

  async ngOnInit() {
    if (this.data) {
      let activitiesArray = this.activitiesForm.get('activities') as FormArray;
      this.data.activities.forEach(activity => {
        activity.startDate = activity.startDate ? new Date(activity.startDate) : null;
        activity.endDate = activity.endDate ? new Date(activity.endDate) : null;
        activity.startUpExemptionStartDate = activity.startUpExemptionStartDate ? new Date(activity.startUpExemptionStartDate) : null;
        activity.startUpExemptionEndDate = activity.startUpExemptionEndDate ? new Date(activity.startUpExemptionEndDate) : null;
        activity.turnoverExemptionStartDate = activity.turnoverExemptionStartDate ? new Date(activity.turnoverExemptionStartDate) : null;
        activity.turnoverExemptionEndDate = activity.turnoverExemptionEndDate ? new Date(activity.turnoverExemptionEndDate) : null;
        activity.activityVariationDate = activity.activityVariationDate ? new Date(activity.activityVariationDate) : null;
        activitiesArray.push(this.fb.group(activity));
      });

      this.data.society.startDateISOObligation = this.data.society.startDateISOObligation ? new Date(this.data.society.startDateISOObligation) : null;
      this.societyForm.patchValue(this.data.society);

      // Load Gran Empresa periods
      if (this.data.society?.isBigCompanyPeriods && this.data.society.isBigCompanyPeriods.length > 0) {
        this.data.society.isBigCompanyPeriods.forEach((period: any) => {
          this.bigCompanyPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load IS 40.3 LIS periods
      if (this.data.society?.paymentOnAccountIS403LISPeriods && this.data.society.paymentOnAccountIS403LISPeriods.length > 0) {
        this.data.society.paymentOnAccountIS403LISPeriods.forEach((period: any) => {
          this.is403LISPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load ROI periods
      if (this.data.other?.intraCommunityOperatorPeriods && this.data.other.intraCommunityOperatorPeriods.length > 0) {
        this.data.other.intraCommunityOperatorPeriods.forEach((period: any) => {
          this.roiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load LEI periods
      if (this.data.other?.codeLEIPeriods && this.data.other.codeLEIPeriods.length > 0) {
        this.data.other.codeLEIPeriods.forEach((period: any) => {
          this.leiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load EORI periods
      if (this.data.other?.codeEORIPeriods && this.data.other.codeEORIPeriods.length > 0) {
        this.data.other.codeEORIPeriods.forEach((period: any) => {
          this.eoriPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load SII periods
      if (this.data.other?.presentationSIIPeriods && this.data.other.presentationSIIPeriods.length > 0) {
        this.data.other.presentationSIIPeriods.forEach((period: any) => {
          this.siiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load Grupo Fiscal periods
      if (this.data.other?.fiscalGroupPeriods && this.data.other.fiscalGroupPeriods.length > 0) {
        this.data.other.fiscalGroupPeriods.forEach((period: any) => {
          this.fiscalGroupPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null,
            taxGroupNumber: period.taxGroupNumber || '',
            isDominantCompany: period.isDominantCompany ?? false,
            dominantCompanyName: period.dominantCompanyName || '',
          }));
        });
      }

      // Load Digital Certificates from fiscal data
      if (this.data.fiscal?.digitalCertificates && this.data.fiscal.digitalCertificates.length > 0) {
        this.hasCertificate = true;
        this.data.fiscal.digitalCertificates.forEach((cert: any) => {
          this.digitalCertificates.push(this.fb.group({
            representative: cert.representative,
            representativeNif: cert.representativeNif,
            expiryDate: cert.expiryDate ? new Date(cert.expiryDate) : null
          }));
        });
      }
      
      this.otherForm.patchValue({
        id: this.data.other?.id,
        administrationNameAEAT: this.data.other?.administrationNameAEAT,
        administrationCodeAEAT: this.data.other?.administrationCodeAEAT,
        delegationAEAT: this.data.other?.delegationAEAT,
        intraCommunityOperatorObligation: this.data.other?.intraCommunityOperatorObligation,
        presentationSIIObligation: this.data.other?.presentationSIIObligation,
        codeLEI: this.data.other?.codeLEI,
        codeEORI: this.data.other?.codeEORI,
        isFiscalGroup: this.data.other?.isFiscalGroup || false,
      });
    }
  }

  get activities(): FormArray {
    return this.activitiesForm.get('activities') as FormArray;
  }
}
